import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Clock } from 'lucide-react';
import apiService from '../../services/apiService';
import {
  broadcastLogout,
  clearAllAuthStorage,
  disableTestTimeoutMode,
  enableTestTimeoutMode,
  getLastActivityMs,
  getSessionTimeoutMs,
  getWarningDurationMs,
  isIdleExpired,
  isTestTimeoutMode,
  msUntilIdleExpiry,
  msUntilIdleWarning,
  subscribeSessionSync,
  touchActivity,
} from '../../utils/sessionManager';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'click', 'scroll', 'touchstart', 'pointerdown'];

/**
 * Idle session timeout with warning modal, cross-tab sync, and tab-visibility checks.
 */
export default function SessionTimeoutHandler() {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(60);

  const warningTimerRef = useRef(null);
  const expiryTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const loggingOutRef = useRef(false);
  const showWarningRef = useRef(false);

  const sessionTimeoutMs = getSessionTimeoutMs();
  const warningDurationMs = getWarningDurationMs(sessionTimeoutMs);
  const warningSeconds = Math.max(1, Math.round(warningDurationMs / 1000));

  showWarningRef.current = showWarning;

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    warningTimerRef.current = null;
    expiryTimerRef.current = null;
    countdownRef.current = null;
  }, []);

  const performLogout = useCallback(
    (reason = 'idle') => {
      if (loggingOutRef.current) return;
      loggingOutRef.current = true;
      clearTimers();
      setShowWarning(false);
      broadcastLogout(reason);
      disableTestTimeoutMode();
      clearAllAuthStorage();
      logout();
      navigate('/login?timeout=true', { replace: true });
    },
    [clearTimers, logout, navigate]
  );

  const openWarning = useCallback(() => {
    if (!isAuthenticated || loggingOutRef.current) return;
    setShowWarning(true);
    setSecondsRemaining(warningSeconds);
  }, [isAuthenticated, warningSeconds]);

  const scheduleIdleTimers = useCallback(() => {
    clearTimers();
    if (!isAuthenticated || loggingOutRef.current) return;

    if (isIdleExpired(sessionTimeoutMs)) {
      performLogout('idle');
      return;
    }

    const untilWarning = msUntilIdleWarning(sessionTimeoutMs);
    const untilExpiry = msUntilIdleExpiry(sessionTimeoutMs);

    if (untilExpiry <= warningDurationMs) {
      openWarning();
      expiryTimerRef.current = setTimeout(() => performLogout('idle'), untilExpiry);
      return;
    }

    warningTimerRef.current = setTimeout(openWarning, untilWarning);
    expiryTimerRef.current = setTimeout(() => performLogout('idle'), untilExpiry);
  }, [
    clearTimers,
    isAuthenticated,
    openWarning,
    performLogout,
    sessionTimeoutMs,
    warningDurationMs,
  ]);

  const extendSession = useCallback(async () => {
    touchActivity(true);
    setShowWarning(false);
    try {
      await apiService.getUserProfile();
    } catch (_) {
      /* still extend client idle window */
    }
    touchActivity(true);
    scheduleIdleTimers();
  }, [scheduleIdleTimers]);

  // Persist test mode from URL
  useEffect(() => {
    if (isTestTimeoutMode()) enableTestTimeoutMode();
  }, [location.search, location.hash]);

  // Bootstrap activity timestamp when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      setShowWarning(false);
      loggingOutRef.current = false;
      return;
    }
    if (getLastActivityMs() === null) touchActivity(true);
    if (isIdleExpired(sessionTimeoutMs)) {
      performLogout('idle');
      return;
    }
    scheduleIdleTimers();
    return clearTimers;
  }, [isAuthenticated, sessionTimeoutMs, scheduleIdleTimers, clearTimers, performLogout]);

  // Activity listeners (ignored while warning modal is open)
  useEffect(() => {
    if (!isAuthenticated) return;

    const onUserActivity = () => {
      if (showWarningRef.current || loggingOutRef.current) return;
      if (touchActivity()) scheduleIdleTimers();
    };

    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onUserActivity, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onUserActivity));
  }, [isAuthenticated, scheduleIdleTimers]);

  // Cross-tab + visibility
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    return subscribeSessionSync(
      () => {
        if (showWarningRef.current || loggingOutRef.current) return;
        if (isIdleExpired(sessionTimeoutMs)) {
          performLogout('idle');
          return;
        }
        scheduleIdleTimers();
      },
      (reason) => {
        if (reason?.startsWith('unauthorized') || reason?.startsWith('idle')) {
          performLogout(reason.split(':')[0] || 'idle');
        }
      }
    );
  }, [isAuthenticated, scheduleIdleTimers, performLogout]);

  // Countdown while warning is visible
  useEffect(() => {
    if (!showWarning) {
      if (countdownRef.current) clearInterval(countdownRef.current);
      return undefined;
    }

    countdownRef.current = setInterval(() => {
      setSecondsRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          performLogout('idle');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [showWarning, performLogout]);

  if (!isAuthenticated || location.pathname === '/login') return null;
  if (!showWarning) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.blob} />

        <div style={styles.iconContainer}>
          <Clock size={40} color="#f59e0b" style={styles.icon} />
        </div>

        <h2 style={styles.title}>Session expiring</h2>
        <p style={styles.description}>
          You have been inactive for a while. For your security, you will be signed out in:
        </p>

        <div style={styles.countdownContainer}>
          <span style={styles.countdown}>{secondsRemaining}</span>
          <span style={styles.seconds}>seconds</span>
        </div>

        <div style={styles.progressBg}>
          <div
            style={{
              ...styles.progressFill,
              width: `${(secondsRemaining / warningSeconds) * 100}%`,
            }}
          />
        </div>

        <div style={styles.buttonGroup}>
          <button type="button" onClick={extendSession} style={styles.keepBtn}>
            Keep session active
          </button>
          <button type="button" onClick={() => performLogout('idle')} style={styles.logoutBtn}>
            Sign out now
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes overlayFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(5deg); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(11, 11, 23, 0.85)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999,
    animation: 'overlayFadeIn 0.3s ease-out',
  },
  modal: {
    position: 'relative',
    width: '90%',
    maxWidth: '440px',
    backgroundColor: 'rgba(23, 23, 47, 0.8)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '24px',
    padding: '36px 32px',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
    textAlign: 'center',
    color: '#fff',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    overflow: 'hidden',
    animation: 'fadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
  },
  blob: {
    position: 'absolute',
    top: '-60px',
    right: '-60px',
    width: '180px',
    height: '180px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(245, 158, 11, 0.15) 0%, transparent 70%)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  iconContainer: {
    position: 'relative',
    zIndex: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    border: '1px solid rgba(245, 158, 11, 0.2)',
    marginBottom: '20px',
    animation: 'float 3s ease-in-out infinite',
  },
  icon: { display: 'block' },
  title: {
    position: 'relative',
    zIndex: 1,
    fontSize: '22px',
    fontWeight: '700',
    color: '#f8fafc',
    margin: '0 0 10px 0',
    letterSpacing: '-0.02em',
  },
  description: {
    position: 'relative',
    zIndex: 1,
    fontSize: '14px',
    color: '#94a3b8',
    margin: '0 0 20px 0',
    lineHeight: '1.6',
  },
  countdownContainer: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  countdown: {
    fontSize: '54px',
    fontWeight: '800',
    color: '#f59e0b',
    lineHeight: '1',
    letterSpacing: '-0.04em',
  },
  seconds: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#d97706',
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    marginTop: '4px',
  },
  progressBg: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    height: '4px',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    borderRadius: '2px',
    overflow: 'hidden',
    marginBottom: '28px',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#f59e0b',
    borderRadius: '2px',
    transition: 'width 1s linear',
  },
  buttonGroup: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  keepBtn: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
  },
  logoutBtn: {
    width: '100%',
    padding: '14px',
    backgroundColor: 'transparent',
    color: '#94a3b8',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};
